import React from "react";
import MenuBar from "./MenuPanel";
import FooterPanel from "./FooterPanel";
import { useTranslation } from 'react-i18next';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation('footer');

  return (
    <React.Fragment>
      {/* Menu */}
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>

      {/* Title Section with Overlay */}
      <section className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">{t('privacyPolicy')}</h1>
          </div>
        </div>
      </section>

      {/* Main Content - Privacy Policy */}
      <main className="container-fluid px-3 px-md-5 py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-9">
            <div className="privacy-content bg-white p-4 p-md-5 rounded shadow-sm">
              
              {/* Introduction */}
              <section className="mb-5">
                <p className="lead">
                  Grace Anointing Catholic Ministries ("we," "our," or "us") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
                  subscribe to our newsletter or interact with our services.
                </p>
                <p className="text-muted">
                  <strong>Last Updated:</strong> February 2026
                </p>
              </section>

              {/* Information We Collect */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">1. Information We Collect</h2>
                <p>When you subscribe to our newsletter, we collect the following information:</p>
                <ul className="list-unstyled ps-3">
                  <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i><strong>Personal Information:</strong> First name, last name, email address</li>
                  <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i><strong>Contact Information:</strong> Phone number (optional)</li>
                  <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i><strong>Preferences:</strong> Country of origin, language preference, regions of interest</li>
                  <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i><strong>Consent Data:</strong> Newsletter subscription consent, age confirmation (13+ years)</li>
                </ul>
              </section>

              {/* How We Use Your Information */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">2. How We Use Your Information</h2>
                <p>We use the information we collect for the following purposes:</p>
                <div className="ps-3">
                  <div className="mb-3">
                    <h5 className="h6"><i className="fas fa-envelope text-info me-2"></i>Newsletter Delivery</h5>
                    <p className="mb-0">To send you our newsletter with updates about retreats, events, spiritual resources, and ministry activities.</p>
                  </div>
                  <div className="mb-3">
                    <h5 className="h6"><i className="fas fa-language text-info me-2"></i>Personalization</h5>
                    <p className="mb-0">To deliver content in your preferred language and relevant to your selected regions.</p>
                  </div>
                  <div className="mb-3">
                    <h5 className="h6"><i className="fas fa-bell text-info me-2"></i>Event Notifications</h5>
                    <p className="mb-0">To inform you about upcoming retreats, conventions, and prayer sessions in your areas of interest.</p>
                  </div>
                  <div className="mb-3">
                    <h5 className="h6"><i className="fas fa-chart-line text-info me-2"></i>Analytics</h5>
                    <p className="mb-0">To understand how our subscribers engage with our content and improve our services.</p>
                  </div>
                </div>
              </section>

              {/* Legal Basis */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">3. Legal Basis for Processing</h2>
                <p>We process your personal information based on:</p>
                <ul className="list-unstyled ps-3">
                  <li className="mb-2"><i className="fas fa-gavel text-warning me-2"></i><strong>Consent:</strong> You have explicitly consented to receive our newsletter</li>
                  <li className="mb-2"><i className="fas fa-gavel text-warning me-2"></i><strong>Legitimate Interest:</strong> To inform our community about ministry activities and spiritual resources</li>
                  <li className="mb-2"><i className="fas fa-gavel text-warning me-2"></i><strong>Legal Compliance:</strong> To comply with COPPA requirements (age 13+ verification)</li>
                </ul>
              </section>

              {/* Data Sharing */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">4. How We Share Your Information</h2>
                <p>We do not sell, trade, or rent your personal information. We may share your information only in the following circumstances:</p>
                <ul className="list-unstyled ps-3">
                  <li className="mb-2"><i className="fas fa-shield-alt text-danger me-2"></i><strong>Service Providers:</strong> With trusted third-party services (e.g., SendGrid for email delivery) who are contractually obligated to protect your data</li>
                  <li className="mb-2"><i className="fas fa-shield-alt text-danger me-2"></i><strong>Event Organizers:</strong> With local church organizers only when you register for specific retreat events</li>
                  <li className="mb-2"><i className="fas fa-shield-alt text-danger me-2"></i><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                </ul>
              </section>

              {/* Your Rights */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">5. Your Rights</h2>
                <p>You have the following rights regarding your personal information:</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="card h-100 border-primary">
                      <div className="card-body">
                        <h5 className="card-title h6"><i className="fas fa-user-edit text-primary me-2"></i>Access & Update</h5>
                        <p className="card-text small">Request access to or update your personal information at any time.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card h-100 border-primary">
                      <div className="card-body">
                        <h5 className="card-title h6"><i className="fas fa-trash-alt text-primary me-2"></i>Data Deletion</h5>
                        <p className="card-text small">Request deletion of your personal information from our systems.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card h-100 border-primary">
                      <div className="card-body">
                        <h5 className="card-title h6"><i className="fas fa-ban text-primary me-2"></i>Unsubscribe</h5>
                        <p className="card-text small">Unsubscribe from our newsletter at any time using the link in our emails.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card h-100 border-primary">
                      <div className="card-body">
                        <h5 className="card-title h6"><i className="fas fa-download text-primary me-2"></i>Data Portability</h5>
                        <p className="card-text small">Request a copy of your data in a portable format.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Data Security */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">6. Data Security</h2>
                <p>We implement appropriate technical and organizational measures to protect your personal information:</p>
                <ul className="list-unstyled ps-3">
                  <li className="mb-2"><i className="fas fa-lock text-success me-2"></i>Encrypted data transmission (HTTPS/SSL)</li>
                  <li className="mb-2"><i className="fas fa-lock text-success me-2"></i>Secure database storage with access controls</li>
                  <li className="mb-2"><i className="fas fa-lock text-success me-2"></i>Regular security audits and updates</li>
                  <li className="mb-2"><i className="fas fa-lock text-success me-2"></i>Limited employee access on a need-to-know basis</li>
                </ul>
              </section>

              {/* Data Retention */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">7. Data Retention</h2>
                <p>
                  We retain your personal information for as long as you remain subscribed to our newsletter. 
                  If you unsubscribe, we will delete your information within 30 days, except where we are required 
                  to retain it for legal or regulatory purposes.
                </p>
              </section>

              {/* Children's Privacy */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">8. Children's Privacy</h2>
                <p>
                  Our newsletter subscription service is intended for individuals aged 13 and older. We require 
                  age confirmation during subscription to comply with the Children's Online Privacy Protection Act (COPPA). 
                  If we become aware that we have collected information from a child under 13 without proper consent, 
                  we will delete that information immediately.
                </p>
              </section>

              {/* International Transfers */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">9. International Data Transfers</h2>
                <p>
                  Grace Anointing Catholic Ministries operates globally. Your information may be transferred to and 
                  processed in countries other than your country of residence. We ensure appropriate safeguards are 
                  in place to protect your personal information in accordance with this Privacy Policy.
                </p>
              </section>

              {/* Changes to Policy */}
              <section className="mb-5">
                <h2 className="h3 text-primary mb-4">10. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any significant changes 
                  by posting the new Privacy Policy on our website and updating the "Last Updated" date. We encourage 
                  you to review this Privacy Policy periodically.
                </p>
              </section>

              {/* Contact Information */}
              <section className="mb-4">
                <h2 className="h3 text-primary mb-4">11. Contact Us</h2>
                <p>If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:</p>
                <div className="card bg-light border-0 mt-3">
                  <div className="card-body">
                    <p className="mb-2"><i className="fas fa-church text-primary me-2"></i><strong>Grace Anointing Catholic Ministries</strong></p>
                    <p className="mb-2"><i className="fas fa-envelope text-primary me-2"></i>Email: <a href="mailto:admin@graceanointing.org">admin@graceanointing.org</a></p>
                    <p className="mb-2"><i className="fas fa-envelope text-primary me-2"></i>Newsletter queries: <a href="mailto:newsletter@graceanointing.org">newsletter@graceanointing.org</a></p>
                    <p className="mb-0"><i className="fas fa-globe text-primary me-2"></i>Website: <a href="https://graceanointing.org" target="_blank" rel="noopener noreferrer">www.graceanointing.org</a></p>
                  </div>
                </div>
              </section>

              {/* Download PDF Option */}
              <section className="text-center mt-5 pt-4 border-top">
                <p className="text-muted mb-3">You can also download this privacy policy as a PDF document:</p>
                <a 
                  href={`${process.env.PUBLIC_URL}/policy/Privacy Policy NewsLetter.pdf`}
                  className="btn btn-outline-primary"
                  download="Privacy_Policy_Newsletter.pdf"
                >
                  <i className="fas fa-file-pdf me-2"></i>Download PDF Version
                </a>
              </section>

            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <FooterPanel />
    </React.Fragment>
  );
};

export default PrivacyPolicyPage;
